<script setup lang="ts">
import MIcon from "../MIcon";
import { computed } from "vue";
import { categoricalSlotClass } from "../../../utils/categoricalPalette";

const props = defineProps({
  photoUrl: String,
  initials: String,
  name: String,
  workspace: Boolean,
  // What the palette slot is derived from, when it should not be the display text. A workspace
  // passes its tenant id, so renaming it does not repaint it.
  colourKey: String,
  size: {
    type: String,
    default: "22px",
  },
});

const text = computed(() => {
  if (props.initials) {
    return props.initials;
  }
  if (props.name) {
    return props.name
      .split(/[^a-zA-ZáàâäãåçéèêëíìîïñóòôöõúùûüýÿæœøūšÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒØŪŠ]/gim)
      .filter(Boolean)
      .map((x) => x[0])
      .join("");
  }

  return "";
});

const initial = computed(() => text.value.slice(0, props.workspace ? 1 : 2).toLocaleUpperCase());
const colour = computed(() => {
  const key = props.colourKey || props.name || props.initials;

  return key ? categoricalSlotClass(key) : "medicus-no-colour";
});
</script>
<template>
  <div
    class="m-avatar medicus-avatar"
    :class="{ [colour]: true, 'workspace-avatar': workspace }"
    :title="name"
    aria-hidden="true"
    data-testid="medicus-avatar"
  >
    <div class="m-avatar__content">
      <template v-if="initial">
        <img v-if="photoUrl" :src="photoUrl" data-testid="assignee-photo" />
        <div class="assignee-initials" data-testid="assignee-initials">
          {{ initial }}
        </div>
      </template>
      <template v-else>
        <m-icon class="no-name-avatar" name="fa-solid fa-user" data-testid="no-assignee-icon" />
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.m-avatar {
  display: inline-flex;
  vertical-align: middle;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-avatar);
  overflow: hidden;

  .m-avatar__content {
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    width: 100%;
    height: 100%;
  }
}

.medicus-avatar {
  font-size: calc(v-bind(size) + 6px);
  width: v-bind(size);
  height: v-bind(size);

  // border: 1px solid var(--border-colour);

  .m-avatar__content {
    font-size: 0.35em;
    display: flex;
    align-items: center;
    text-align: center;
    letter-spacing: 0.01em;
    text-transform: capitalize;

    width: 100%;
    height: 100%;

    .assignee-initials {
      font-weight: bold;
    }
  }

  &.workspace-avatar {
    // margin-right: 5px;
    width: v-bind(size);
    height: v-bind(size);
    border-radius: var(--radius-default);
    display: flex;
    align-items: center;
    justify-content: center;

    .m-avatar__content {
      font-size: var(--font-size-md) !important;
      color: var(--grey-0) !important;

      // NOTE reset the margin
      margin-left: 0;

      line-height: initial;
    }

    .m-avatar__content > .no-name-avatar {
      font-size: var(--font-size-md) !important;
      color: var(--text-color-lightest) !important;
    }
  }

  // ENG-21740 Categorical palette, now shared from css/_categorical-palette.scss so the workspace
  // bar and the patient finder badge resolve the same slot for the same workspace.
  &[class*="m-categorical-"] {
    background: var(--m-categorical-background) !important;
    color: var(--m-categorical-foreground) !important;
  }
  &.medicus-no-colour {
    background: var(--grey-lightest) !important;
    color: var(--text-color-lightest) !important;
  }

  // --

  // --

  //--
}
</style>
